import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Ghost, Skull, Star, Map, Ban, Target, ArrowRight } from "lucide-react";

const kindLabel = { video: "Video Idea", challenge: "Challenge", build: "Build" };

export default function SharedItem() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/share/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (notFound) return <div className="py-32 text-center"><Ghost className="mx-auto h-10 w-10 text-primary" /><p className="mt-4 font-display text-3xl tracking-wider">Vanished into the fog</p><p className="text-foreground/50">This shared idea no longer exists.</p></div>;

  const d = item.data;

  return (
    <div data-testid="shared-item-page" className="mx-auto max-w-3xl px-6 py-14 md:px-10">
      <div className="mb-6 flex items-center justify-between">
        <Link to={`/u/${item.username}`} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground/60 hover:text-primary">
          <Skull className="h-4 w-4 text-primary" /> Shared by @{item.username}
        </Link>
        <span className="border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{kindLabel[item.kind]}</span>
      </div>

      <div className="border border-primary/30 bg-card p-7 md:p-9">
        {item.kind === "video" && (
          <>
            {d.category && <span className="border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{d.category}</span>}
            <h1 className="mt-4 font-display text-4xl leading-tight tracking-wide md:text-5xl">{d.title}</h1>
            {d.hook && <p className="mt-3 text-lg italic text-primary/90">“{d.hook}”</p>}
            <p className="mt-4 text-foreground/70">{d.description}</p>
            {Array.isArray(d.tips) && <ul className="mt-5 space-y-2">{d.tips.map((t) => <li key={t} className="flex gap-3 text-sm text-foreground/70"><span className="mt-1 h-1.5 w-1.5 shrink-0 bg-primary" />{t}</li>)}</ul>}
          </>
        )}

        {item.kind === "challenge" && (
          <>
            <div className="flex flex-wrap gap-2">
              <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{d.role}</span>
              <span className="border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{d.difficulty}</span>
              {d.character && <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{d.character}</span>}
            </div>
            <h1 className="mt-4 font-display text-4xl tracking-wide md:text-5xl">{d.title}</h1>
            <p className="mt-3 text-foreground/70">{d.description}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <Box icon={Ban} label="Perks" value={d.perk_restriction} />
              <Box icon={Map} label="Map" value={d.map_restriction} />
              <Box icon={Target} label="Objective" value={d.objective} />
            </div>
            {Array.isArray(d.rules) && <ul className="mt-5 space-y-2">{d.rules.map((r) => <li key={r} className="flex gap-3 text-sm text-foreground/70"><span className="mt-1 h-1.5 w-1.5 shrink-0 bg-primary" />{r}</li>)}</ul>}
          </>
        )}

        {item.kind === "build" && (
          <>
            <div className="flex flex-wrap gap-2">
              <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{d.role}</span>
              <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{d.character}</span>
              <span className="border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{d.playstyle}</span>
            </div>
            <div className="mt-4 flex items-end justify-between gap-4">
              <h1 className="font-display text-4xl tracking-wide md:text-5xl">{d.build_name}</h1>
              {d.strength_rating != null && <div className="flex shrink-0 items-center gap-1.5 text-primary"><Star className="h-5 w-5 fill-primary" /><span className="font-display text-2xl">{d.strength_rating}/10</span></div>}
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {Array.isArray(d.perks) && d.perks.map((p, i) => (
                <div key={p.name || i} className="border border-white/10 bg-background/50 p-4">
                  <div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span><p className="font-bold">{p.name}</p></div>
                  <p className="mt-2 text-sm text-foreground/60">{p.reason}</p>
                </div>
              ))}
            </div>
            {d.strategy && <div className="mt-6 border-l-2 border-primary bg-background/40 p-4"><p className="mb-1 text-xs font-bold uppercase tracking-widest text-primary">Strategy</p><p className="text-sm text-foreground/70">{d.strategy}</p></div>}
          </>
        )}
      </div>

      <div className="mt-8 border border-primary/20 bg-card p-6 text-center">
        <p className="font-display text-2xl tracking-wide">Want to generate your own?</p>
        <Button asChild className="mt-4 rounded-sm bg-primary font-bold uppercase tracking-widest hover:bg-primary/90">
          <Link to="/">Explore DBD Creator Hub <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    </div>
  );
}

const Box = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-background/50 p-4">
    <div className="flex items-center gap-2 text-primary"><Icon className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">{label}</span></div>
    <p className="mt-2 text-sm text-foreground/70">{value}</p>
  </div>
);
